// ============================================================
// Reading maps out of a folder, in bulk.
//
// The sibling of `folderImport.ts`, and it makes the same promise:
// A BAD FILE IS ONE MAP. Import twenty and the nineteen good ones
// arrive; the failure is named and counted rather than aborting the
// batch or vanishing into `console.error`.
//
// THE LATEST VERSION IS THE MAP. A map folder is a timeline -
// `001.json`, `002-taller-walls.json` - and bulk import wants the
// current one. `treeFromFileList` has already chosen it; the Workshop
// is where you go back through the history. The caller may hand a
// different `file` in to pin an older one.
//
// Every file goes through `readMapFile`, the same reader the session's
// own single-file import uses. It never throws: it returns
// `{ maps, error }`, so a character JSON picked up by mistake is a
// named failure rather than an exception.
// ============================================================
import { readMapFile } from '../engine/battlefield/mapFile';
import type { MapEntry } from '../engine/battlefield/mapFile';
import type { FileCharacter } from './folderFiles';

export interface MapFolderImport {
  maps: MapEntry[];
  /** Folders that could not be read at all, or held nothing usable. */
  failed: string[];
}

/** The maps one folder's file holds, named for the folder when it is one; null when it holds none. */
async function mapsIn(entry: FileCharacter): Promise<MapEntry[] | null> {
  const { maps: found, error } = readMapFile(JSON.parse(await entry.file.text()));
  if (error || found.length === 0) return null;
  // THE FOLDER NAME WINS, but only when the file holds exactly one
  // map. The folder is what you saw in the shelf and what you
  // picked, and a file whose inner name has drifted should not
  // import under the old one. A file holding several cannot be
  // renamed by one folder, so those keep their own names.
  return found.length === 1 ? [{ ...found[0], name: entry.name }] : found;
}

/**
 * One entry per map FOLDER, read into the maps it holds.
 *
 * `entries` is `FileCharacter[]` because a maps folder has exactly the
 * character folder's shape - `<Map>/<version>.json`, optionally inside
 * a group - so `treeFromFileList` parses it unchanged and there is no
 * second tree builder to keep in step. The type's name is the only
 * thing about it that is character-specific.
 */
export async function readMapsFromFiles(entries: readonly FileCharacter[]): Promise<MapFolderImport> {
  const maps: MapEntry[] = [];
  const failed: string[] = [];

  for (const entry of entries) {
    const found = await mapsIn(entry).catch(() => null);
    if (found) maps.push(...found);
    else failed.push(entry.name);
  }

  return { maps, failed };
}
